// Valuta på bilag. Bokføring skjer alltid i NOK, men et bilag kan være i
// fremmed valuta. Da er `totalGross` originalbeløpet og `totalGrossNok` det
// eneste lovlige bokføringsgrunnlaget.
//
// Bakgrunnen er konkret: en kvittering på 125 USD ble bokført som 125 kr fordi
// `currency` ble lest ut av analysen, men aldri brukt nedstrøms. Reglene her er
// derfor bevisst strenge — mangler NOK-beløpet, skal bilaget stoppes, ikke
// gjettes på.
(function initReceiptCurrency(global) {
  const num = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  // «KR» er ikke en valutakode, men det er det som står på bilaget, og analysen
  // kan levere det ordrett. Uten oversettelsen ble hvert norske bilag merket
  // «Bilaget er i KR og mangler omregnet NOK-beløp» — en valutakontroll på
  // kroner. Hva «kr» betyr avhenger av landet foretaket bokfører i.
  const LOCAL_CURRENCY_WORDS = new Set(['KR', 'KR.', 'KRONER', 'KRONE', 'NKR', 'SKR', 'DKR', ',-']);
  const LOCAL_CURRENCY_BY_COUNTRY = { NO: 'NOK', SE: 'SEK', DK: 'DKK' };
  const localCurrency = () => {
    const country = String(global.PlummeBackend?.appState?.settings?.country || 'NO').toUpperCase();
    return LOCAL_CURRENCY_BY_COUNTRY[country] || 'NOK';
  };

  const normalize = (value) => {
    const raw = String(value || '').trim().toUpperCase();
    if (!raw) return localCurrency();
    return LOCAL_CURRENCY_WORDS.has(raw) ? localCurrency() : raw;
  };

  // 'UNKNOWN' settes av OCR når flere valutaer er like sterkt representert i
  // dokumentet. Den er utenlandsk i denne sammenhengen: den er ikke foretakets
  // egen valuta, og beløpet kan ikke bokføres uten manuell avklaring.
  const isForeign = (value) => normalize(value) !== localCurrency();

  // Returnerer beløpet som lovlig kan bokføres, eller null.
  const grossNok = (analysis) => {
    if (!analysis) return null;
    const nok = num(analysis.totalGrossNok);
    if (isForeign(analysis.currency)) return nok > 0 ? nok : null;
    const gross = nok > 0 ? nok : num(analysis.totalGross);
    return gross > 0 ? gross : null;
  };

  // Et bilag som har et beløp, men ikke noe NOK-beløp, venter på omregning.
  const needsConversion = (analysis) =>
    Boolean(analysis) && num(analysis.totalGross) > 0 && grossNok(analysis) == null;

  const reviewReason = (analysis) => {
    if (!needsConversion(analysis)) return '';
    const code = normalize(analysis.currency);
    if (code === 'UNKNOWN') return 'Valutaen på bilaget er uklar. Beløpet må avklares før bokføring.';
    return `Bilaget er i ${code} og mangler omregnet NOK-beløp.`;
  };

  const money = (value, currency) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return '';
    const decimals = Math.abs(value - Math.round(value)) > 0.004 ? 2 : 0;
    // `currency: null` gir beløp uten suffiks — riktig når valutaen er ukjent,
    // siden et påklistret «kr» er nettopp feilen dette skal hindre.
    return global.PlummeI18n.money(value, currency === undefined ? { decimals } : { decimals, currency });
  };

  // Visningstekst for innboksen. Utenlandske bilag viser originalbeløpet, og
  // NOK-beløpet i parentes først når det faktisk er omregnet.
  const formatAmount = (analysis, fallbackAmount) => {
    const value = analysis && analysis.totalGross != null ? analysis.totalGross : fallbackAmount;
    if (!isForeign(analysis && analysis.currency)) return money(value);
    const code = normalize(analysis.currency);
    const original = money(value, code === 'UNKNOWN' ? null : code);
    const nok = money(grossNok(analysis));
    return nok ? `${original} (${nok})` : original;
  };

  const formatVatAmount = (analysis) => {
    const value = Number(analysis?.vatAmount);
    if (!Number.isFinite(value) || value < 0) return '';
    if (!isForeign(analysis?.currency)) return money(value);
    const code = normalize(analysis.currency);
    const original = money(value, code === 'UNKNOWN' ? null : code);
    const nok = money(Number(analysis.vatAmountNok));
    return nok ? `${original} (${nok})` : original;
  };

  // Regner et redigert originalbeløp om til NOK med kursen som allerede ligger
  // på bilaget. Uten kurs returneres null, slik at bilaget forblir sperret for
  // bokføring i stedet for å arve et NOK-beløp som hørte til det gamle beløpet.
  const convert = (analysis, totalGross) => {
    const gross = num(totalGross);
    if (!(gross > 0)) return null;
    if (!isForeign(analysis && analysis.currency)) return gross;
    const rate = num(analysis && analysis.exchangeRate);
    if (!(rate > 0)) return null;
    return Math.round(gross * rate * 100) / 100;
  };

  const roundMoney = (value) => Math.round((num(value) + Number.EPSILON) * 100) / 100;
  const amountInMode = (gross, factor, mode) => mode === 'ink' ? num(gross) : roundMoney(num(gross) / factor);
  const amountFromMode = (value, factor, mode) => mode === 'ink' ? num(value) : roundMoney(num(value) * factor);
  const syncEditedAmounts = (previous, next) => {
    const changed = ['totalGross', 'totalGrossNok', 'vatRate', 'reverseChargeCode', 'vatTreatment'].some(key => String(previous?.[key] ?? '') !== String(next?.[key] ?? ''));
    if (!changed) return next;
    const factor = global.PlummePurchaseVatTreatment.grossFactor(next);
    const original = num(next.totalGross), local = grossNok(next);
    return { ...next, purchaseLines: null, purchaseLineValidation: null,
      vatAmount: roundMoney(original - roundMoney(original / factor)),
      vatAmountNok: local == null ? null : roundMoney(local - roundMoney(local / factor)) };
  };
  // Keep explicit edits and their dependent fields when refreshing machine suggestions.
  const mergeReanalysis = (previous, fresh) => {
    const fields = new Set(previous?.userEditedFields || []);
    const groups = [
      ['totalGrossNok', 'totalGross', 'currency', 'exchangeRate', 'exchangeRateDate', 'exchangeRateSource'],
      ['vatRate', 'reverseChargeCode', 'vatTreatment'],
      ['suggestedCostAccountCode', 'suggestedCostAccountName'],
      ['suggestedPaymentSource', 'paymentAccountCode', 'paymentSourceOrigin', 'paymentEvidence'],
    ];
    for (const group of groups) if (group.some(field => fields.has(field))) group.forEach(field => fields.add(field));
    const merged = { ...fresh };
    for (const field of fields) merged[field] = previous[field] ?? null;
    if (fields.size) {
      merged.userEditedFields = [...fields]; merged.userEditedAt = previous.userEditedAt;
      merged.receiptWorkflowStatus = 'manual_review'; merged.approvalRoute = 'manual_review';
      // Old/new line amounts cannot be combined after an explicit amount or VAT edit.
      if (fields.has('totalGrossNok') || fields.has('vatRate')) {
        merged.purchaseLines = null; merged.purchaseLineValidation = null;
        const factor = global.PlummePurchaseVatTreatment.grossFactor(merged);
        const gross = grossNok(merged);
        merged.vatAmountNok = gross == null ? null : roundMoney(gross - roundMoney(gross / factor));
        merged.vatAmount = isForeign(merged.currency) ? roundMoney(num(merged.totalGross) - num(merged.totalGross) / factor) : merged.vatAmountNok;
      }
      if (fields.has('suggestedCostAccountCode') && Array.isArray(merged.purchaseLines)) merged.purchaseLines = merged.purchaseLines.map(line => ({ ...line, costAccountCode: merged.suggestedCostAccountCode, costAccountName: merged.suggestedCostAccountName }));
    }
    return merged;
  };

  global.PlummeReceiptCurrency = {
    roundMoney, amountInMode, amountFromMode, mergeReanalysis, syncEditedAmounts,
    normalize,
    isForeign,
    grossNok,
    convert,
    needsConversion,
    reviewReason,
    formatAmount,
    formatVatAmount,
  };
})(typeof window !== 'undefined' ? window : globalThis);
